import React, { useState, useEffect } from "react";

import { LoadingIcon, InfoSmIcon, Typography } from "@packages/ds";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import Error from "Components/Error";
import { IconControl, Button, ButtonWrapper, Link } from "ds/Button";
import Dialog from "ds/Dialog";
import * as Input from "ds/Input";
import * as Sticker from "ds/Sticker";
import { Tooltip } from "ds/Tooltip";
import useDecodedParams from "Hooks/useDecodedParams";
import useInput from "Hooks/useInput";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import useUrls from "Hooks/useUrls";
import { isJson, capitalize, isDefined } from "Libs/utils";
import { canManageEnvironmentVariablesSelector } from "Reducers/environment";
import { canManageProjectVariablesSelector } from "Reducers/project/project";
import { useDeploymentType } from "Reducers/project/project/hooks/useDeploymentType";
import { setProjectContext } from "Reducers/settingsMenu";
import { getCommonError } from "Reducers/sliceFactory";

import * as S from "./VariableForm.styles";

import type { Variable as ClientVariable, Environment } from "@packages/client";

export type EnvError = Record<string, string | undefined>;

type Variable = Pick<
  ClientVariable,
  | "hasPermission"
  | "id"
  | "is_enabled"
  | "inherited"
  | "is_inheritable"
  | "is_json"
  | "is_sensitive"
  | "name"
  | "value"
  | "visible_build"
  | "visible_runtime"
>;

export type VariableFormProps = {
  editable?: boolean;
  environment?: Environment;
  errors?: Error | EnvError;
  isChanged: boolean;
  isChangedUpdate: () => void;
  isInactive?: boolean;
  isLoading: boolean;
  isOverridingFrom?: string | boolean;
  linkToEdit?: string;
  onCancel: () => void;
  onDelete: () => void;
  onSave: (data: Partial<Variable>) => void;
  originEnv?: string;
  variable?: Partial<Variable>;
  cancelSignal?: boolean;
  variablePage: "project" | "environment";
};

const VariableForm = ({
  editable = true,
  environment,
  errors = {},
  isChanged,
  isChangedUpdate,
  isInactive = false,
  isLoading,
  isOverridingFrom = false,
  linkToEdit,
  onCancel,
  onDelete,
  onSave,
  originEnv,
  variable,
  cancelSignal,
  variablePage
}: VariableFormProps) => {
  const intl = useIntl();
  const { organizationId, projectId, environmentId } = useDecodedParams<{
    organizationId: string;
    projectId: string;
    environmentId: string;
  }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { shouldShowDeploymentType, isAutomaticDeploymentEnabled } =
    useDeploymentType({ environmentId });

  const [valueError, setValueError] = useState<string>();
  const isNew = !variable?.id;
  const { inputs, handleInputChange, setInputs, initialValues } = useInput(
    {
      is_inheritable: isDefined((variable as Variable)?.is_inheritable)
        ? (variable as Variable)?.is_inheritable
        : true,
      is_json: isDefined(variable?.is_json) ? variable?.is_json : false,
      is_sensitive: isDefined(variable?.is_sensitive)
        ? variable?.is_sensitive
        : false,
      is_enabled: isDefined((variable as Variable)?.is_enabled)
        ? (variable as Variable)?.is_enabled
        : true,
      name: isNew ? "env:" : variable?.name || "",
      value: variable?.value || "",
      visible_build: isDefined(variable?.visible_build)
        ? variable?.visible_build
        : !environment,
      visible_runtime: isDefined(variable?.visible_runtime)
        ? variable?.visible_runtime
        : true
    },
    isChangedUpdate
  );
  const [valueFocused, setValueFocused] = useState(false);

  useEffect(() => {
    setInputs(initialValues);
    // "initialValues" cannot be added as a dependency or it will prevent the form from updating
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelSignal, setInputs]);

  const [envVariableUrl, projectVariableUrl] = useUrls(
    [
      "organization.project.environment.settings.variables.variable",
      "organization.project.settings.variables.variable"
    ],
    {
      organizationId: organizationId ?? "",
      projectId: projectId ?? "",
      environmentId: originEnv ?? "",
      variableId: variable?.id ?? ""
    }
  );

  const isRequired = isNew || (!isNew && !variable.is_sensitive);
  const showSaveAndCancelButtons = isChanged;
  const showDeleteButton =
    editable && variable?.hasPermission && variable?.hasPermission("#delete");

  const canManageEnvironmentVariables = useSelectorWithUrlParams(
    canManageEnvironmentVariablesSelector
  );

  const canManageProjectVariables = useSelectorWithUrlParams(
    canManageProjectVariablesSelector
  );

  const canManageVariables =
    variablePage === "environment"
      ? canManageEnvironmentVariables
      : canManageProjectVariables;

  const handleFocus = () => {
    setValueFocused(true);
  };

  const handleBlur = () => {
    setValueFocused(false);
  };

  const handleProjectVariableEdit = () => {
    dispatch(setProjectContext());
    if (linkToEdit) {
      navigate(linkToEdit);
    }
  };

  const handleSave = e => {
    e?.preventDefault();

    if (isNew && (!inputs.name || !inputs.value)) return;
    if (!canManageVariables) return;

    // sensitive var doesn't display value
    if (!isNew && (!inputs.name || (!inputs.is_sensitive && !inputs.value)))
      return;
    if (!isJson(inputs.value) && inputs.is_json) {
      setValueError(
        intl.formatMessage({
          id: `settings.variables.value_error`
        })
      );
      return;
    }
    let data = {
      id: variable?.id || "",
      name: inputs.name,
      // Sensitive variables don't expose their value in the API, so we need to guard against emptying the value accidentally.
      ...(inputs.value !== "" && { value: inputs.value }),
      is_json: inputs.is_json,
      is_sensitive: inputs.is_sensitive,
      visible_build: inputs.visible_build,
      visible_runtime: inputs.visible_runtime
    };
    if (environment) {
      data = Object.assign(data, {
        inherited: (variable as Variable)?.inherited || false,
        is_enabled: inputs.is_enabled,
        is_inheritable: inputs.is_inheritable
      });
    }
    onSave(data);
  };

  const saveBtn = (
    <Button
      analyticId={
        isNew
          ? "settings.variables.add.button"
          : (variable as Variable)?.inherited
            ? "settings.variables.override"
            : "save"
      }
      id="save-variable-btn"
      type="submit"
      onClick={handleSave}
      disabled={!canManageVariables}
    >
      {capitalize(
        intl.formatMessage({
          id: isNew
            ? "settings.variables.add.button"
            : (variable as Variable)?.inherited
              ? "settings.variables.override"
              : "save"
        })
      )}
    </Button>
  );

  const saveEl = canManageVariables ? (
    saveBtn
  ) : (
    <Tooltip
      tooltip={intl.formatMessage({
        id: `${variablePage}.variable.access`
      })}
      analyticId={`${variablePage}.variable.access`}
    >
      {saveBtn}
    </Tooltip>
  );

  const textareaLabel = intl.formatMessage({ id: "value" });

  const commonError = getCommonError(errors).error;
  const errorMessage = commonError?.message;
  const warningText = `settings.${
    environment ? "environment." : ""
  }variables.warning${(variable as Variable)?.inherited ? "" : shouldShowDeploymentType && !isAutomaticDeploymentEnabled ? ".automatic_disabled" : ""}${(variable as Variable)?.inherited ? ".child" : ""}`;

  return (
    <S.Wrapper>
      <S.FormWrapper aria-labelledby="edit-variable-heading">
        {Object.keys(errors ?? {}).length !== 0 && (
          <Error>{errorMessage}</Error>
        )}

        {isInactive && (
          <S.Inactive size="extraSmall" weight="semiBold" $color="granite">
            {intl.formatMessage({ id: "settings.variables.inactive.details" })}
          </S.Inactive>
        )}
        {isOverridingFrom ? (
          <S.Inherited>
            <S.OverridingFrom to={projectVariableUrl}>
              {intl.formatMessage(
                {
                  id: `settings.variables.overriding_from.${isOverridingFrom}`
                },
                { from: originEnv }
              )}
            </S.OverridingFrom>
          </S.Inherited>
        ) : (
          <>
            {environment &&
              !isNew &&
              ((variable as Variable)?.inherited ||
                !(variable as Variable)?.is_enabled) && (
                <S.Inherited>
                  <S.InheritedFrom to={envVariableUrl}>
                    {intl.formatMessage(
                      { id: "settings.variables.inherited_from" },
                      { from: originEnv }
                    )}
                  </S.InheritedFrom>
                  <S.Toggle>
                    <label htmlFor="is_enabled">
                      {intl.formatMessage({
                        id: "settings.variables.enabled"
                      })}
                    </label>
                    <input
                      type="checkbox"
                      id="is_enabled"
                      name="is_enabled"
                      checked={inputs.is_enabled}
                      onChange={elt => {
                        handleInputChange(elt);
                      }}
                    />
                  </S.Toggle>
                </S.Inherited>
              )}
          </>
        )}

        <S.InputWrapper>
          <S.InputRoot>
            <S.InputLabelWrapper>
              <S.Label
                required
                htmlFor="name"
                isValid={!errors["name"]}
                disabled={!isNew}
              >
                {intl.formatMessage({ id: "settings.variables.name" })}
              </S.Label>
              {!isNew && (
                <Dialog
                  description={intl.formatMessage({
                    id: "settings.variables.edit_name"
                  })}
                  analyticId="settings.variables.edit_name"
                  link={
                    <Link
                      href={intl.formatMessage({
                        id: "links.documentation.variables"
                      })}
                      analyticId="learnmore"
                    >
                      {intl.formatMessage({ id: "learnmore" })}
                    </Link>
                  }
                />
              )}
            </S.InputLabelWrapper>
            <Input.Input
              id="name"
              name="name"
              value={inputs.name}
              onChange={elt => {
                handleInputChange(elt);
              }}
              disabled={!isNew}
              required
              isValid={!errors["name"]}
              width="100%"
            />
            {errors?.["name"] && <Input.Error>{errors["name"]}</Input.Error>}
          </S.InputRoot>
        </S.InputWrapper>

        <S.NoteWrapper>
          <Typography.Label $color="default-weak">
            {intl.formatMessage({
              id: "settings.variables.notes.title",
              defaultMessage: "Note:"
            })}
          </Typography.Label>
          <S.NoteLabel textStyle="extraSmall" data-testid="form-notes">
            {intl.formatMessage<React.ReactElement>(
              {
                id: "settings.variables.notes.detail_beginning"
              },
              {
                code: chunk => <S.Code variant="inline">{chunk}</S.Code>
              }
            )}
          </S.NoteLabel>
          <S.NoteLabel textStyle="extraSmall" data-testid="form-notes">
            {intl.formatMessage(
              {
                id: "settings.variables.notes.detail_different"
              },
              {
                appEnvironmentVariableName: (
                  <S.Code variant="inline">
                    {intl.formatMessage({
                      id: "app_environment_variable_name"
                    })}
                  </S.Code>
                ),
                link: chunk => (
                  <S.NotesLink
                    variant="link"
                    href={intl.formatMessage({
                      id: "links.documentation.settings.variables.variable.prefix"
                    })}
                  >
                    {chunk}
                  </S.NotesLink>
                )
              }
            )}
          </S.NoteLabel>
          {variablePage === "environment" && (
            <S.NoteLabel textStyle="extraSmall" data-testid="form-notes">
              {intl.formatMessage({
                id: "settings.variables.notes.detail_adding",
                defaultMessage:
                  "Adding or updating an environment level variable will trigger a redeploy."
              })}
            </S.NoteLabel>
          )}
        </S.NoteWrapper>

        <S.InputWrapper>
          <S.Textarea
            id="value"
            name="value"
            label={textareaLabel}
            placeholder={intl.formatMessage(
              { id: "textarea.placeholder.default" },
              {
                label: textareaLabel
              }
            )}
            value={
              variable?.is_sensitive && !valueFocused && !inputs.value
                ? "*****"
                : inputs.value
            }
            required={isRequired}
            onChange={elt => {
              handleInputChange(elt);
              setValueError(undefined);
            }}
            onFocus={handleFocus}
            onBlur={handleBlur}
            error={errors["value"] || valueError}
            disabled={!editable}
            height={240}
          />
        </S.InputWrapper>

        <S.Option>
          <S.VariableCheckbox
            forId="visible_runtime"
            label={intl.formatMessage<React.ReactElement>(
              {
                id: "settings.variables.during_run"
              },
              {
                b: (...chunks) => <strong>{chunks}</strong>
              }
            )}
            checked={inputs.visible_runtime}
            onCheckedChanged={checked => {
              handleInputChange({
                target: {
                  type: "checkbox",
                  checked,
                  name: "visible_runtime"
                }
              });
            }}
            disabled={!editable}
          />
          <Dialog
            analyticId="settings.variables.during_run.more"
            title={intl.formatMessage({ id: "learnmore" })}
            description={intl.formatMessage({
              id: "settings.variables.during_run.more"
            })}
            align="center"
            trigger={
              <IconControl aria-label="info-trigger-dunning-run">
                <InfoSmIcon />
              </IconControl>
            }
            link={
              <Button
                analyticId="learnmore"
                variant="link"
                href={intl.formatMessage({
                  id: "links.documentation.variables.run_and_build_time"
                })}
              >
                {intl.formatMessage({ id: "learnmore" })}
              </Button>
            }
          />
        </S.Option>

        <S.Option>
          <S.VariableCheckbox
            forId="visible_build"
            label={intl.formatMessage<React.ReactElement>(
              {
                id: "settings.variables.during_build"
              },
              {
                b: (...chunks) => <strong>{chunks}</strong>,
                code: (...chunks) => <S.Code variant="inline">{chunks}</S.Code>,
                appConfigFile: intl.formatMessage({ id: "app_config_file" })
              }
            )}
            checked={inputs.visible_build}
            onCheckedChanged={checked => {
              handleInputChange({
                target: {
                  type: "checkbox",
                  checked,
                  name: "visible_build"
                }
              });
            }}
            disabled={!editable}
          />
          <Dialog
            title={intl.formatMessage({ id: "learnmore" })}
            description={intl.formatMessage({
              id: "settings.variables.during_build.more"
            })}
            align="center"
            trigger={
              <IconControl aria-label="info-trigger-learn">
                <InfoSmIcon />
              </IconControl>
            }
            link={
              <Button
                variant="link"
                href={intl.formatMessage({
                  id: "links.documentation.variables.run_and_build_time"
                })}
              >
                {intl.formatMessage({ id: "learnmore" })}
              </Button>
            }
          />
        </S.Option>

        <S.Option>
          <S.VariableCheckbox
            forId="is_json"
            label={intl.formatMessage<React.ReactElement>(
              {
                id: "settings.variables.is_json"
              },
              {
                b: (...chunks) => <strong>{chunks}</strong>
              }
            )}
            checked={inputs.is_json}
            onCheckedChanged={checked => {
              handleInputChange({
                target: {
                  type: "checkbox",
                  checked,
                  name: "is_json"
                }
              });
            }}
            disabled={!editable}
          />{" "}
          <Dialog
            title={intl.formatMessage({ id: "learnmore" })}
            description={intl.formatMessage({
              id: "settings.variables.is_json.more"
            })}
            align="center"
            trigger={
              <IconControl aria-label="info-trigger-learn-more">
                <InfoSmIcon />
              </IconControl>
            }
            link={
              <Button
                variant="link"
                href={intl.formatMessage({
                  id: "links.documentation.variables.run_and_build_time"
                })}
              >
                {intl.formatMessage({ id: "learnmore" })}
              </Button>
            }
          />
        </S.Option>

        <S.Option>
          <S.VariableCheckbox
            forId="is_sensitive"
            label={intl.formatMessage<React.ReactElement>(
              {
                id: "settings.variables.sensitive"
              },
              {
                b: (...chunks) => <strong>{chunks}</strong>
              }
            )}
            checked={inputs.is_sensitive}
            onCheckedChanged={checked => {
              handleInputChange({
                target: {
                  type: "checkbox",
                  checked,
                  name: "is_sensitive"
                }
              });
            }}
            disabled={!editable || variable?.is_sensitive}
          />{" "}
          <Dialog
            title={intl.formatMessage({ id: "learnmore" })}
            description={intl.formatMessage({
              id: "settings.variables.sensitive.more"
            })}
            align="center"
            trigger={
              <IconControl aria-label="info-trigger-sensitive-more">
                <InfoSmIcon />
              </IconControl>
            }
            link={
              <Button
                variant="link"
                href={intl.formatMessage({
                  id: "links.documentation.variables"
                })}
              >
                {intl.formatMessage({ id: "learnmore" })}
              </Button>
            }
          />
        </S.Option>

        {environment && (
          <S.Option>
            <S.VariableCheckbox
              forId="is_inheritable"
              label={intl.formatMessage<React.ReactElement>(
                {
                  id: "settings.variables.inheritable"
                },
                {
                  b: (...chunks) => <strong>{chunks}</strong>
                }
              )}
              checked={inputs.is_inheritable}
              onCheckedChanged={checked => {
                handleInputChange({
                  target: {
                    type: "checkbox",
                    checked,
                    name: "is_inheritable"
                  }
                });
              }}
              disabled={!editable}
            />{" "}
            <Dialog
              title={intl.formatMessage({ id: "learnmore" })}
              description={intl.formatMessage({
                id: "settings.variables.inheritable.more"
              })}
              align="center"
              trigger={
                <IconControl aria-label="info-trigger-inheritable">
                  <InfoSmIcon />
                </IconControl>
              }
              link={
                <Button
                  variant="link"
                  href={intl.formatMessage({
                    id: "links.documentation.variables"
                  })}
                >
                  {intl.formatMessage({ id: "learnmore" })}
                </Button>
              }
            />
          </S.Option>
        )}

        {!isNew && showSaveAndCancelButtons && (
          <S.WarningSticker priority="normal">
            <Sticker.Icon priority="normal" />
            <Sticker.Text>
              {intl.formatMessage({
                id: warningText
              })}
            </Sticker.Text>
          </S.WarningSticker>
        )}

        <ButtonWrapper spacing="modal">
          {isLoading ? (
            <LoadingIcon />
          ) : (
            <>
              {!editable && (
                <Button
                  id="edit-variable-btn"
                  onClick={handleProjectVariableEdit}
                >
                  {capitalize(
                    intl.formatMessage({
                      id: "edit"
                    })
                  )}
                </Button>
              )}

              {(isNew
                ? inputs.name && inputs.value && showSaveAndCancelButtons
                : showSaveAndCancelButtons) && (
                <>
                  {saveEl}
                  <Button
                    id="cancel-variable-btn"
                    variant="secondary"
                    onClick={onCancel}
                  >
                    {capitalize(intl.formatMessage({ id: "cancel" }))}
                  </Button>
                </>
              )}

              {showDeleteButton && (
                <S.DeleteButton
                  analyticId="delete"
                  variant="outline"
                  id={`variable-list-${variable.name}-delete-btn`}
                  onClick={onDelete}
                >
                  {capitalize(intl.formatMessage({ id: "delete" }))}
                </S.DeleteButton>
              )}
            </>
          )}
        </ButtonWrapper>
      </S.FormWrapper>
    </S.Wrapper>
  );
};

export default VariableForm;
